import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  completeLoginLink,
  sendLoginLink,
  sendPasswordReset,
  signInWithEmail,
  signInWithGoogle,
} from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { Logo } from "../components/Logo";

export function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One screen at a time: three methods stacked on one page confused buyers.
  const [screen, setScreen] = useState<"start" | "password" | "linkSent" | "resetSent">("start");
  const go = (next: typeof screen) => {
    setError(null);
    setScreen(next);
  };

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // arriving from the email link → finish sign-in, then the effect above redirects
  useEffect(() => {
    completeLoginLink().catch((e) => setError(friendlyError(e)));
  }, []);

  function guardEmail(): string | null {
    const value = email.trim();
    if (!value) {
      setError("กรอกอีเมลที่ใช้ตอนซื้อก่อนนะคะ");
      return null;
    }
    return value;
  }

  async function run(fn: () => Promise<void>) {
    setSubmitting(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  }

  const handleGoogle = () => run(signInWithGoogle);

  const handleLink = () => {
    const value = guardEmail();
    if (!value) return;
    return run(async () => {
      await sendLoginLink(value);
      go("linkSent");
    });
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    return run(async () => {
      await signInWithEmail(email.trim(), password);
    });
  };

  const handleReset = () => {
    const value = guardEmail();
    if (!value) return;
    return run(async () => {
      await sendPasswordReset(value);
      go("resetSent");
    });
  };

  const back = (
    <button type="button" className="btn-ghost auth-back" onClick={() => go("start")}>
      ← ย้อนกลับ
    </button>
  );

  const sentScreen = (title: string, note: string, resend: () => void) => (
    <div className="auth-sent">
      <div className="auth-sent-icon" aria-hidden>✉️</div>
      <h2>{title}</h2>
      <p>
        เราส่งลิงก์ไปที่ <strong>{email.trim()}</strong> แล้ว
        <br />
        {note}
      </p>
      <p className="auth-hint">ไม่เห็นอีเมล? รอ 1-2 นาที แล้วเช็กใน Spam หรือ Promotions ด้วยนะคะ</p>
      {/@gmail\.com$/i.test(email.trim()) && (
        <a className="btn" href="https://mail.google.com/" target="_blank" rel="noreferrer">
          เปิด Gmail
        </a>
      )}
      <button type="button" className="btn-secondary" onClick={resend} disabled={submitting}>
        {submitting ? "กำลังส่ง…" : "ส่งอีกครั้ง"}
      </button>
      {error && <p className="error">{error}</p>}
      <button type="button" className="btn-ghost" onClick={() => go("start")}>
        ← ย้อนกลับ / ใช้อีเมลอื่น
      </button>
    </div>
  );

  const emailField = (
    <label>
      อีเมลที่ใช้ตอนซื้อ
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
        placeholder="you@example.com"
      />
    </label>
  );

  return (
    <main>
      <div className="auth-page">
        <Logo withSchool />

        {screen === "linkSent" ? (
          sentScreen("เช็กอีเมลของคุณ", "กดลิงก์ในอีเมลเพื่อเข้าเรียนได้เลย ไม่ต้องใช้รหัสผ่าน", handleLink)
        ) : screen === "resetSent" ? (
          sentScreen("เช็กอีเมลของคุณ", "กดลิงก์ในอีเมลเพื่อตั้งรหัสผ่านใหม่", handleReset)
        ) : screen === "password" ? (
          <>
            {back}
            <h1>เข้าด้วยรหัสผ่าน</h1>
            <p className="auth-sub">ใช้รหัสผ่านที่ได้ทางอีเมลตอนซื้อคอร์ส</p>
            <form onSubmit={handleLogin}>
              {emailField}
              <label>
                รหัสผ่าน
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </label>
              <button type="submit" disabled={submitting}>
                {submitting ? "กำลังเข้าสู่ระบบ…" : "เข้าเรียน →"}
              </button>
            </form>
            {error && <p className="error" style={{ marginTop: "1rem" }}>{error}</p>}
            <p style={{ marginTop: "1.25rem", fontSize: "0.9rem" }}>
              <button type="button" className="link-btn" onClick={handleReset} disabled={submitting}>
                ลืมรหัสผ่าน? ส่งลิงก์ตั้งรหัสใหม่
              </button>
            </p>
          </>
        ) : (
          <>
            <h1 style={{ marginTop: "1.5rem" }}>
              เริ่มเรียน<span className="accent" style={{ color: "var(--c-green)" }}>กันเลย</span>
            </h1>
            <p className="auth-sub">เข้าด้วยอีเมลเดียวกับที่ใช้ตอนซื้อคอร์ส</p>

            <button type="button" onClick={handleGoogle} disabled={submitting}>
              เข้าด้วย Google
            </button>
            <p className="auth-hint">ซื้อด้วย Gmail? กดปุ่มนี้ง่ายที่สุด</p>

            <p className="auth-divider">หรือใช้อีเมลอื่น</p>
            {emailField}
            <button type="button" className="btn-primary" onClick={handleLink} disabled={submitting}>
              {submitting ? "กำลังส่ง…" : "ส่งลิงก์เข้าเรียนทางอีเมล"}
            </button>
            <p className="auth-hint">ไม่ต้องจำรหัส กดลิงก์ในอีเมลก็เข้าได้เลย</p>

            {error && <p className="error" style={{ marginTop: "1rem" }}>{error}</p>}

            <p className="auth-alt">
              ได้รหัสผ่านทางอีเมลตอนซื้อ?{" "}
              <button type="button" className="link-btn" onClick={() => go("password")}>
                เข้าด้วยรหัสผ่าน →
              </button>
            </p>
          </>
        )}
      </div>
    </main>
  );
}

function friendlyError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (msg.includes("auth/invalid-credential") || msg.includes("auth/wrong-password")) {
    return "อีเมลหรือรหัสผ่านไม่ถูกต้อง";
  }
  if (msg.includes("auth/user-not-found")) {
    return "ไม่พบบัญชีอีเมลนี้: เช็กว่าซื้อคอร์สแล้วและใช้อีเมลที่ลงทะเบียนกับ Stripe";
  }
  if (msg.includes("auth/too-many-requests")) {
    return "ลองเข้าสู่ระบบหลายครั้งเกินไป รออีกสักครู่แล้วลองใหม่";
  }
  if (msg.includes("auth/popup-closed-by-user") || msg.includes("auth/cancelled-popup-request")) {
    return "หน้าต่าง Google ถูกปิดไปก่อน ลองกดอีกครั้งนะคะ";
  }
  if (msg.includes("auth/unauthorized-domain")) {
    return "โดเมนนี้ยังไม่ได้เปิดให้ล็อกอินด้วย Google แจ้งแอดมินให้เพิ่มโดเมนใน Firebase ก่อนนะคะ";
  }
  if (msg.includes("auth/operation-not-allowed")) {
    return "วิธีเข้าสู่ระบบนี้ยังไม่ได้เปิดใช้ในระบบ แจ้งแอดมินด้วยนะคะ";
  }
  return msg;
}
